import { ReactNode, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { messagesSelectors } from 'store/messages'
import { useGetChat } from 'api/chats'
import { useSendBotQuestion } from 'hooks/useSendBotQuestion'
import { ChatDetail } from 'components/chats/ChatDetail'
import { ChatBottomBar } from 'components/ChatBottomBar'
import { ChatTextInput } from 'components/ChatTextInput'
import { ChatPageHeader } from 'components/chat-page/ChatPageHeader'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { ChatHero } from 'components/chat-page/ChatHero'
import { Spinner } from '@/components/ui/spinner'

type ChatMainAreaProps = {
  areaId?: string
  /** Notebook mode: new chats are locked to this agent. */
  agentId?: string
  contextId?: string
  /** Notebook mode: lets the page flush its own pending prompt once the new chat's messages are loaded. */
  onMessagesLoaded?: () => void
  /** Buttons at the top-right: in the chat header row once a chat exists, floating over the hero before. */
  headerActions?: ReactNode
}

export const ChatMainArea = ({
  areaId,
  agentId,
  contextId,
  onMessagesLoaded: onMessagesLoadedProp,
  headerActions,
}: ChatMainAreaProps) => {
  const chatId = useSelector(messagesSelectors.chatId)
  const currentChat = useSelector(messagesSelectors.currentChat)
  const { sendQuestion } = useSendBotQuestion()

  useGetChat(chatId)

  const pendingQuestionRef = useRef<string | undefined>(undefined)

  const onMessagesLoaded = useCallback(() => {
    const question = pendingQuestionRef.current
    if (question) {
      pendingQuestionRef.current = undefined
      sendQuestion(question).catch(console.error)
    }
    onMessagesLoadedProp?.()
  }, [sendQuestion, onMessagesLoadedProp])

  if (!chatId) {
    return (
      <>
        <div className="absolute top-2 left-2 z-10">
          <SidebarTrigger />
        </div>
        {headerActions && <div className="absolute top-2 right-2 z-10">{headerActions}</div>}
        <ChatHero
          areaId={areaId}
          agentId={agentId}
          contextId={contextId}
          onPendingQuestion={(question) => {
            pendingQuestionRef.current = question
          }}
          onPendingCancelled={() => {
            pendingQuestionRef.current = undefined
          }}
        />
      </>
    )
  }

  if (!currentChat) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <ChatPageHeader actions={headerActions} />
      <div data-surface="page" className="relative mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col px-2 lg:px-4">
        <ChatDetail subscribeUserChannel={false} onMessagesLoaded={onMessagesLoaded} />
        <ChatBottomBar surface="page">
          <ChatTextInput />
        </ChatBottomBar>
      </div>
    </>
  )
}
